import { useEffect, useMemo, useState } from "react";
import { ArrowRight, MessageCircle, Printer, Wallet } from "lucide-react";
import { PageHeader, StatusPill } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { api, errorText, inr, formatDate, todayISO } from "../lib/api";
import { buildWhatsAppLink } from "../components/WhatsAppBubble";

function receiptMessage(data) {
  const library = data.library || {};
  const student = data.student || {};
  const payment = data.payment || {};
  return [
    `Hi ${student.name || "there"},`,
    "",
    `Thanks for your payment at ${library.name || "LibraryOS"}.`,
    `Receipt: ${payment.receipt_number}`,
    `Amount: ₹${payment.amount}`,
    `Mode: ${payment.payment_mode}`,
    `Date: ${payment.payment_date}`,
    payment.payment_for ? `For: ${payment.payment_for}` : null,
    "",
    library.footer_text || "Powered by MKS Digital Technology",
  ].filter(Boolean).join("\n");
}

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

function ReceiptView({ data, close }) {
  const message = receiptMessage(data);
  const waLink = buildWhatsAppLink(data.student?.mobile, message);
  const print = () => {
    const win = window.open("", "_blank", "width=640,height=800");
    if (!win) return;
    const library = data.library || {};
    const student = data.student || {};
    const payment = data.payment || {};
    const html = `
      <title>Receipt ${escapeHtml(payment.receipt_number)}</title>
      <style>body{font-family:Inter,sans-serif;padding:40px;color:#0f172a}h1{letter-spacing:-.02em}table{width:100%;border-collapse:collapse;margin-top:20px}td{padding:8px 0;border-bottom:1px solid #e2e8f0}strong{color:#1d4ed8}</style>
      <h1>${escapeHtml(library.name || "LibraryOS")}</h1>
      <p>${escapeHtml(library.address || "")} ${escapeHtml(library.city || "")}</p>
      <h2>Receipt ${escapeHtml(payment.receipt_number)}</h2>
      <table>
        <tr><td>Student</td><td><strong>${escapeHtml(student.name)} (${escapeHtml(student.student_id)})</strong></td></tr>
        <tr><td>Amount</td><td><strong>₹${escapeHtml(payment.amount)}</strong></td></tr>
        <tr><td>Payment mode</td><td>${escapeHtml(payment.payment_mode)}</td></tr>
        <tr><td>Payment date</td><td>${escapeHtml(payment.payment_date)}</td></tr>
        <tr><td>Payment for</td><td>${escapeHtml(payment.payment_for)}</td></tr>
      </table>
      <p style='margin-top:30px;color:#64748b;font-size:12px'>Powered by MKS Digital Technology</p>
    `;
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.print();
  };
  return (
    <Modal title={`Receipt ${data.payment.receipt_number}`} eyebrow="PAYMENT" close={close} testId="receipt-modal">
      <div className="receipt">
        <div className="receipt-row"><span>Student</span><strong>{data.student?.name}</strong></div>
        <div className="receipt-row"><span>Student ID</span><strong>{data.student?.student_id}</strong></div>
        <div className="receipt-row"><span>Amount</span><strong>{inr(data.payment.amount)}</strong></div>
        <div className="receipt-row"><span>Mode</span><strong>{data.payment.payment_mode}</strong></div>
        <div className="receipt-row"><span>Date</span><strong>{formatDate(data.payment.payment_date)}</strong></div>
        <div className="receipt-row"><span>For</span><strong>{data.payment.payment_for}</strong></div>
      </div>
      <div className="modal-actions">
        <button className="button button-outline" onClick={close}>Close</button>
        <a className="button button-whatsapp" href={waLink} target="_blank" rel="noopener noreferrer" data-testid="receipt-whatsapp-share"><MessageCircle size={16} /> Send on WhatsApp</a>
        <button className="button button-primary" onClick={print} data-testid="print-receipt-button"><Printer size={16} /> Print</button>
      </div>
    </Modal>
  );
}

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [show, setShow] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [form, setForm] = useState({ student_id: "", amount: "", payment_mode: "CASH", payment_for: "MONTHLY_FEE", notes: "" });
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [paymentsRes, studentsRes] = await Promise.all([api.get("/payments"), api.get("/students")]);
      setPayments(paymentsRes.data.data);
      setStudents(studentsRes.data.data);
    } catch (err) {
      setError(errorText(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const totalCollection = useMemo(() => payments.reduce((sum, p) => sum + Number(p.amount || 0), 0), [payments]);

  const submit = async (event) => {
    event.preventDefault();
    try {
      await api.post("/payments", { ...form, amount: Number(form.amount) });
      setShow(false);
      setForm({ student_id: "", amount: "", payment_mode: "CASH", payment_for: "MONTHLY_FEE", notes: "" });
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const openReceipt = async (payment) => {
    try {
      const res = await api.get(`/payments/${payment.id}/receipt`);
      setReceipt(res.data.data);
    } catch (err) {
      setError(errorText(err));
    }
  };

  const exportCsv = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/export/payments`, "_blank");
  };

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow="FINANCIAL CONTROL"
        title="Payments"
        description="Record every collection with a printable receipt and keep the ledger tidy."
        action={
          <div style={{ display: "flex", gap: 10 }}>
            <button className="button button-outline" onClick={exportCsv} data-testid="export-payments-button">Export CSV</button>
            <button className="button button-primary" onClick={() => setShow(true)} data-testid="record-payment-button">
              <Wallet size={16} /> Record payment
            </button>
          </div>
        }
      />
      {error ? <div className="error-message">{error}</div> : null}

      <section className="content-card table-card">
        <div className="table-toolbar">
          <div>
            <span className="card-kicker">COLLECTIONS</span>
            <h2>{payments.length} payments · {inr(totalCollection)}</h2>
          </div>
        </div>
        <div className="table-wrap">
          <table data-testid="payments-table">
            <thead>
              <tr><th>Receipt</th><th>Student</th><th>Amount</th><th>Mode</th><th>For</th><th>Date</th><th>Action</th></tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.id} data-testid={`payment-row-${payment.id}`}>
                  <td><strong>{payment.receipt_number}</strong></td>
                  <td>{payment.student_name}<br /><small style={{ color: "#64748b" }}>{payment.student_code}</small></td>
                  <td>{inr(payment.amount)}</td>
                  <td>{payment.payment_mode}</td>
                  <td>{payment.payment_for}</td>
                  <td>{formatDate(payment.payment_date)}</td>
                  <td>
                    <div className="action-row">
                      <button className="button button-outline button-small" onClick={() => openReceipt(payment)} data-testid={`view-receipt-${payment.id}`}>
                        <Printer size={14} /> Receipt
                      </button>
                      <a
                        className="button button-whatsapp button-small"
                        href={buildWhatsAppLink(payment.student_mobile, `Hi ${payment.student_name || ""}, your payment of ₹${payment.amount} is received. Receipt: ${payment.receipt_number}`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`whatsapp-payment-${payment.id}`}
                      >
                        <MessageCircle size={14} /> Share
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {show ? (
        <Modal title="Record payment" eyebrow="NEW PAYMENT" close={() => setShow(false)} testId="record-payment-modal">
          <form className="modal-form" onSubmit={submit}>
            <label>Student
              <select required value={form.student_id} onChange={(event) => setForm({ ...form, student_id: event.target.value })} data-testid="payment-student-select">
                <option value="">Select student</option>
                {students.map((student) => (
                  <option key={student.id} value={student.id}>{student.name} · {student.student_id}</option>
                ))}
              </select>
            </label>
            <div className="form-split">
              <label>Amount<input type="number" min="1" required value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} data-testid="payment-amount-input" /></label>
              <label>Mode
                <select value={form.payment_mode} onChange={(event) => setForm({ ...form, payment_mode: event.target.value })} data-testid="payment-mode-select">
                  <option value="CASH">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="BANK_TRANSFER">Bank transfer</option>
                  <option value="CARD">Card</option>
                  <option value="OTHER">Other</option>
                </select>
              </label>
            </div>
            <label>For
              <select value={form.payment_for} onChange={(event) => setForm({ ...form, payment_for: event.target.value })}>
                <option value="MONTHLY_FEE">Monthly fee</option>
                <option value="ADMISSION">Admission</option>
                <option value="RENEWAL">Renewal</option>
                <option value="LATE_FEE">Late fee</option>
                <option value="OTHER">Other</option>
              </select>
            </label>
            <label>Notes<input value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} /></label>
            <div className="modal-actions">
              <button type="button" className="button button-outline" onClick={() => setShow(false)}>Cancel</button>
              <button className="button button-primary" type="submit" data-testid="save-payment-button">
                Save payment <ArrowRight size={16} />
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {receipt ? <ReceiptView data={receipt} close={() => setReceipt(null)} /> : null}
    </div>
  );
}
